import { kebabCase } from '@makemydeal/dr-common-utils';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { utils } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CUSTOM_MILEAGE_ERROR, CUSTOM_MILEAGE_LABEL, CUSTOM_MILEAGE_MAX_LENGTH, CUSTOM_MILEAGE_TESTID } from '../../../constants';
import { dealLockHooks } from '../../../hooks';
import { convertToNumber } from '../../../utils/formatUtils';
import { usePaymentCall } from '../../../utils/usePaymentCall';
import { getMessageMileage } from './useCustomMileageField';

export const useCustomMileageNumericField = () => {
    const enableLeaseContractField = useSelector(featureToggleSelectors.isEnableLeaseContractFields);
    const isPaymentError = useSelector(offerReduxSelectors.getIsError);
    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);
    const isLocked = dealLockHooks.useIsDealLocked();
    const paymentErrorMessages = useSelector(utils.getPaymentErrorMessages);
    const messagesFromCurrentTerm = useSelector(offerReduxSelectors.getMessagesFromCurrentTerm);

    const [maxValue, setMaxValue] = useState(CUSTOM_MILEAGE_MAX_LENGTH);
    const [slideOut, setSlideOut] = useState(false);

    const toggleSlideOut = useCallback(() => {
        setSlideOut(!slideOut);
    }, [slideOut]);

    useEffect(() => {
        if (!isCalculatingPayment) {
            const errors = isPaymentError ? paymentErrorMessages : messagesFromCurrentTerm;
            const value = getMessageMileage(errors);
            if (value) {
                setMaxValue(value);
            }
        }
    }, [isCalculatingPayment, isPaymentError, JSON.stringify(messagesFromCurrentTerm), paymentErrorMessages]);

    const checkRange = useCallback(
        (selectedValue: number): boolean => {
            if (selectedValue === 0) {
                return true;
            }
            return Boolean(selectedValue && selectedValue > 0 && selectedValue <= maxValue);
        },
        [maxValue]
    );

    const paymentCall = usePaymentCall(offerReduxSelectors.getAnnualMiles, offerReduxActionCreators.selectedAnnualMiles, {
        parseValue: convertToNumber,
        formatValueToString: (v: any) => (v === null || v === undefined ? '' : String(v)),
        ignoreTextValue: (text: string) => {
            const num = convertToNumber(text);
            // ignore dispatch when value is non-zero and outside allowed range
            return num !== 0 && !checkRange(num);
        },
        holdWhileTermResponsePending: true
    });

    const showValueNumeric = useMemo(() => convertToNumber(paymentCall.value), [paymentCall.value]);

    const isError = useMemo(() => {
        if (isCalculatingPayment || showValueNumeric === 0) {
            return false;
        }
        return !checkRange(showValueNumeric);
    }, [checkRange, isCalculatingPayment, showValueNumeric]);

    const handleBlurNumeric = () => {
        if (!checkRange(showValueNumeric)) {
            return;
        }
        paymentCall.handleBlur({ target: { value: paymentCall.value } } as any);
    };

    const handleKeyDown = (event: any) => {
        if (event?.key === 'Enter' && !checkRange(showValueNumeric)) {
            return;
        }
        paymentCall.handleKeyDown(event);
    };

    return {
        label: CUSTOM_MILEAGE_LABEL,
        isLocked,
        hasOptions: enableLeaseContractField,
        isSlideOutOpen: slideOut,
        toggleSlideOut,
        inputProps: {
            id: kebabCase(CUSTOM_MILEAGE_LABEL),
            name: kebabCase(CUSTOM_MILEAGE_LABEL),
            value: paymentCall.value,
            onChange: paymentCall.handleChange,
            onBlur: handleBlurNumeric,
            onKeyDown: handleKeyDown,
            size: 'sm' as const,
            hasError: isError,
            errorMessage: CUSTOM_MILEAGE_ERROR + maxValue,
            disabled: isLocked,
            'data-testid': kebabCase(CUSTOM_MILEAGE_TESTID),
            selectOnFocusSelector: featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap
        }
    };
};
